<template>
	<div class="w-full bg-accented min-w-screen relative isolate overflow-hidden pb-4">
		<div class="text-center pt-16">
			<div class="text-6xl font-semibold text-primary tracking-tight">
				{{ $t('testimonials.title1') }}
			</div>
			<div class="text-6xl font-medium text-highlighted tracking-tight">
				{{ $t('testimonials.title2') }}
			</div>
		</div>
		<div class="column-1 md:columns-2 lg:columns-4 gap-4 w-full max-w-container mx-auto max-h-[850px] mask-linear-[0deg,transparent_10%,black,transparent_95%] px-4 sm:px-6 lg:px-8">
			<!-- Single column layout (mobile) - show all 12 testimonials -->
			<template v-if="currentColumns === 1">
				<UPageMarquee
					pause-on-hover
					reverse
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:90s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials"
						:key="index"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="URLS[index]"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
			</template>

			<!-- Two column layout (tablet) - show all 12 testimonials (6 per column) -->
			<template v-else-if="currentColumns === 2">
				<UPageMarquee
					pause-on-hover
					reverse
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:50s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(0, 6)"
						:key="index"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="URLS[index]"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
				<UPageMarquee
					pause-on-hover
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:60s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(6, 12)"
						:key="index + 6"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="URLS[index + 6]"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
			</template>

			<!-- Four column layout (desktop) - show all 12 testimonials (3 per column) -->
			<template v-else>
				<UPageMarquee
					pause-on-hover
					reverse
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:30s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(0, 3)"
						:key="index"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="URLS[index]"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
				<UPageMarquee
					pause-on-hover
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:40s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(3, 6)"
						:key="index + 3"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="URLS[index + 3]"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
				<UPageMarquee
					pause-on-hover
					reverse
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:45s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(6, 9)"
						:key="index + 6"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="URLS[index + 6]"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
				<UPageMarquee
					pause-on-hover
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:50s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(9, 12)"
						:key="index + 9"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="URLS[index + 9]"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { Testimonial } from '@/types/components/testimonial'

import {
	MOCK_TESTIMONIAL,
	NAMES,
	QUOTES,
	URLS
} from '@/constants/components/testimonials'

const testimonials = ref<Testimonial[]>([])
const intervalId = ref<NodeJS.Timeout>()
const previousIndex = ref<number | null>(null)
const windowWidth = ref(0)

// Determine current number of columns based on screen size
const currentColumns = computed(() => {
	if (windowWidth.value >= 1024) return 4 // lg breakpoint
	if (windowWidth.value >= 768) return 2 // md breakpoint
	return 1 // mobile
})

// Update window width on resize
const updateWindowWidth = () => {
	if (import.meta.client) {
		windowWidth.value = window.innerWidth
	}
}

// Clear all highlights from testimonials
const clearAllHighlights = () => {
	testimonials.value.forEach((t: Testimonial) => t.highlight = false)
}

// Pick a new random testimonial to highlight (different from the previous one)
const cycleHighlight = () => {
	let index: number
	do {
		index = Math.floor(Math.random() * testimonials.value.length)
	} while (index === previousIndex.value)

	clearAllHighlights()

	// Set highlight on the newly selected testimonial
	const current = testimonials.value[index]
	if (current) {
		current.highlight = true
	}

	// Remember this index for next time
	previousIndex.value = index
}

// Initialize testimonials with varied content
const initializeTestimonials = () => {
	// Create 12 testimonials (or however many quotes/names we have)
	const count = Math.min(QUOTES.length, NAMES.length)

	testimonials.value = []

	for (let i = 0; i < count; i++) {
		// Create a deep copy of the mock testimonial
		const newTestimonial: Testimonial = JSON.parse(JSON.stringify(MOCK_TESTIMONIAL))

		// Set unique data for this testimonial
		newTestimonial.user.name = NAMES[i] ?? 'Unknown User'
		// newTestimonial.user.description = createEmail(NAMES[i] ?? 'Unknown User')
		newTestimonial.user.avatar.alt = NAMES[i] ?? 'Unknown User'
		newTestimonial.quote = QUOTES[i] ?? 'No quote available'

		// Generate a random rating between 4.5 and 5.0
		// newTestimonial.rating = parseFloat((Math.random() * (5 - 4.5) + 4.5).toFixed(1))

		// only picking 5 star reviews for initial launch
		newTestimonial.rating = 5

		// Add to testimonials array
		testimonials.value.push(newTestimonial)
	}

	// Initialize first highlighted testimonial
	cycleHighlight()
}

onMounted(() => {
	initializeTestimonials()
	updateWindowWidth()
	window.addEventListener('resize', updateWindowWidth)
	intervalId.value = setInterval(cycleHighlight, 10000) // Change highlight every 10 seconds
})

onBeforeUnmount(() => {
	if (intervalId.value) {
		clearInterval(intervalId.value)
	}
	if (import.meta.client) {
		window.removeEventListener('resize', updateWindowWidth)
	}
})
</script>
